import './index.css';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import theme from '@theme';
import EditorLayout from '@Layout/EditorLayout';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { useEffect, useMemo } from 'react';
import EditorProvider from '@Editor/EditorProvider';
import { SnackbarProvider } from 'notistack';
import { elements } from '@elements';
import { createInstance } from 'i18next';
import en from '@locals/en.json';
import he from '@locals/he.json';
import { merge } from 'lodash';

const rtlCache = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
    key: 'mui',
});

const createI18nInstance = lang => {
    const instance = createInstance();
    instance.init({
        supportedLngs: ['en', 'he'],
        resources: {
            en: { translation: en },
            he: { translation: he },
        },
        lng: lang,
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false,
        },
    });
    return instance;
};

function EmailEditor({
    lang,
    initialData,
    SelectTemplate,
    savedBlocks = [],
    updateBlock,
    saveBlock,
    deleteBlock,
    uploadFile,
    mergeTags = {},
    mergeTagsLabels = {},
    publicTemplates = [],
    savedTemplates = [],
    designs = [],
    saveTemplate,
    title = '',
    setTitle,
    deleteTemplate,
    updateTemplate,
    saveWithHtml,
    saving,
    selectTamplateOpen,
    onSave = () => {},
    navigateBack = () => {},
}) {
    const i18nInstance = useMemo(() => createI18nInstance(lang), [lang]);
    const dir = i18nInstance.dir();
    const rtl = dir === 'rtl';

    return (
        <I18nextProvider i18n={i18nInstance}>
            <CacheProvider value={rtl ? rtlCache : ltrCache}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <Box dir={dir} sx={{ height: '100vh' }}>
                        <SnackbarProvider>
                            <EditorProvider
                                initialData={
                                    initialData || [
                                        merge(elements.html.node, {
                                            theme: { lang },
                                        }),
                                    ]
                                }
                                SelectTemplate={SelectTemplate}
                                onSave={onSave}
                                savedBlocks={savedBlocks}
                                updateBlock={updateBlock}
                                saveBlock={saveBlock}
                                deleteBlock={deleteBlock}
                                uploadFile={uploadFile}
                                mergeTags={mergeTags}
                                mergeTagsLabels={mergeTagsLabels}
                                publicTemplates={publicTemplates}
                                savedTemplates={savedTemplates}
                                saveTemplate={saveTemplate}
                                designs={designs}
                                title={title}
                                setTitle={setTitle}
                                deleteTemplate={deleteTemplate}
                                updateTemplate={updateTemplate}
                                navigateBack={navigateBack}
                                saveWithHtml
                                saving={saving}
                                selectTamplateOpen={selectTamplateOpen}
                            >
                                <EditorLayout />
                            </EditorProvider>
                        </SnackbarProvider>
                    </Box>
                </ThemeProvider>
            </CacheProvider>
        </I18nextProvider>
    );
}

export default EmailEditor;
